/**
 * What the header indicator shows about Brain updates running in the
 * background: one line per update, with how long it has been going and what it
 * is doing right now. Pure, so the wording and the clock formatting are tested.
 */
import type { IndexingJobSpec, IndexingOutcome } from './brainIndexingJob';

/** `sending`: the request that does the work is open. `confirming`: the server stopped waiting; the browser asks the index until it catches up. */
export type JobPhase = 'sending' | 'confirming';

export interface ActivityJob {
  id: number;
  spec: Pick<IndexingJobSpec, 'action' | 'label' | 'paths'>;
  startedAt: number;
  phase: JobPhase;
  /** Set once the update ended. */
  outcome?: IndexingOutcome['kind'];
  finishedAt?: number;
}

export type ActivityStatus = 'running' | 'success' | 'warning' | 'error';

export interface ActivityLine {
  status: ActivityStatus;
  title: string;
  detail: string;
}

/** "12 s", "1 min 05 s", "2 h 03 min": short, and never negative if clocks disagree. */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} min ${String(total % 60).padStart(2, '0')} s`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
}

function subject(job: ActivityJob): string {
  const { label, paths } = job.spec;
  return paths.length > 1 ? `«${label}» (${paths.length} documentos)` : `«${label}»`;
}

export function describeJob(job: ActivityJob, now: number): ActivityLine {
  const removing = job.spec.action === 'deindex';

  if (job.outcome) {
    const took = formatElapsed((job.finishedAt ?? now) - job.startedAt);
    if (job.outcome === 'success') {
      return {
        status: 'success',
        title: removing ? `${subject(job)} quitado del índice` : `${subject(job)} indexado`,
        detail: `Terminado en ${took}`,
      };
    }
    if (job.outcome === 'failed') {
      return {
        status: 'error',
        title: `${subject(job)}: no se pudo ${removing ? 'quitar del índice' : 'indexar'}`,
        detail: `Falló a los ${took}. El documento está guardado.`,
      };
    }
    return {
      status: 'warning',
      title: `${subject(job)}: sin confirmar`,
      detail: `No se pudo confirmar a los ${took}; puede seguir actualizándose.`,
    };
  }

  const elapsed = formatElapsed(now - job.startedAt);
  if (job.phase === 'confirming') {
    return {
      status: 'running',
      title: `${subject(job)}: esperando confirmación`,
      detail: `El servidor sigue trabajando; comprobando el índice · ${elapsed}`,
    };
  }
  return {
    status: 'running',
    title: removing ? `Quitando ${subject(job)} del índice` : `Indexando ${subject(job)}`,
    detail: removing
      ? `Borrando fragmentos y conexiones del grafo · ${elapsed}`
      : `Troceando, calculando embeddings y extrayendo el grafo · ${elapsed}`,
  };
}

/** The status of one update: running until it has an outcome. */
export function statusOf(job: ActivityJob): ActivityStatus {
  if (!job.outcome) return 'running';
  if (job.outcome === 'success') return 'success';
  return job.outcome === 'failed' ? 'error' : 'warning';
}

/** How many updates are still running. */
export function runningCount(jobs: readonly ActivityJob[]): number {
  return jobs.filter((job) => !job.outcome).length;
}

/** The status the indicator itself shows: working beats a failure beats a warning beats done. */
export function overallStatus(jobs: readonly ActivityJob[]): ActivityStatus | null {
  if (jobs.length === 0) return null;
  const statuses = jobs.map(statusOf);
  for (const status of ['running', 'error', 'warning', 'success'] as const) {
    if (statuses.includes(status)) return status;
  }
  return null;
}
